/** Stroke icons on a 20px grid. */
import type { SVGProps } from 'react';

type P = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 16, children, ...rest }: P & { children: React.ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden {...rest}>
      {children}
    </svg>
  );
}

export const Icons = {
  Home: (p: P) => (
    <Icon {...p}>
      <rect x="3" y="3" width="6" height="6" rx="1.5" />
      <rect x="11" y="3" width="6" height="6" rx="1.5" />
      <rect x="3" y="11" width="6" height="6" rx="1.5" />
      <rect x="11" y="11" width="6" height="6" rx="1.5" />
    </Icon>
  ),
  Inbox: (p: P) => (
    <Icon {...p}>
      <path d="M3 11.5l1.8-6.2A1.5 1.5 0 0 1 6.2 4.2h7.6a1.5 1.5 0 0 1 1.4 1.1L17 11.5V15a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 15z" />
      <path d="M3 11.5h4l1 2h4l1-2h4" />
    </Icon>
  ),
  Sparkle: (p: P) => (
    <Icon {...p}>
      <path d="M10 2.8l1.6 4.1 4.1 1.6-4.1 1.6L10 14.2l-1.6-4.1-4.1-1.6 4.1-1.6z" />
      <path d="M15.5 13.5l.6 1.5 1.5.6-1.5.6-.6 1.5-.6-1.5-1.5-.6 1.5-.6z" />
    </Icon>
  ),
  Lasso: (p: P) => (
    <Icon {...p}>
      <path d="M11.2 12.6c3.4-.4 5.9-2.2 5.8-4.6C16.9 5.4 13.7 3.4 9.8 3.5 5.9 3.6 2.9 5.8 3 8.4c.1 1.9 1.8 3.4 4.2 4" />
      <path d="M7.2 12.4c-.9.9-.8 2.2.3 2.8 1 .6 1.1 1.8.3 2.8" />
      <circle cx="9.2" cy="12.5" r="1.9" />
    </Icon>
  ),
  Book: (p: P) => (
    <Icon {...p}>
      <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H16v12H5.5A1.5 1.5 0 0 0 4 16.5z" />
      <path d="M4 16.5A1.5 1.5 0 0 0 5.5 18H16v-3" />
      <path d="M8 7h5M8 10h3" />
    </Icon>
  ),
  List: (p: P) => (
    <Icon {...p}>
      <path d="M7.5 5.5H17M7.5 10H17M7.5 14.5H17" />
      <circle cx="4" cy="5.5" r=".8" fill="currentColor" />
      <circle cx="4" cy="10" r=".8" fill="currentColor" />
      <circle cx="4" cy="14.5" r=".8" fill="currentColor" />
    </Icon>
  ),
  CheckCircle: (p: P) => (
    <Icon {...p}>
      <circle cx="10" cy="10" r="7" />
      <path d="M7 10.2l2 2 4-4.2" />
    </Icon>
  ),
  Users: (p: P) => (
    <Icon {...p}>
      <circle cx="8" cy="7" r="2.8" />
      <path d="M2.8 16.5c.6-2.6 2.7-4.2 5.2-4.2s4.6 1.6 5.2 4.2" />
      <path d="M13 4.5a2.6 2.6 0 0 1 0 5M14.6 12.4c1.4.5 2.4 1.8 2.7 4.1" />
    </Icon>
  ),
  Doc: (p: P) => (
    <Icon {...p}>
      <path d="M5 2.8h6.5L15.5 7v9.7a.5.5 0 0 1-.5.5H5a.5.5 0 0 1-.5-.5V3.3a.5.5 0 0 1 .5-.5z" />
      <path d="M11.5 2.8V7h4M7.5 10.5h5M7.5 13.5h5" />
    </Icon>
  ),
  Receipt: (p: P) => (
    <Icon {...p}>
      <path d="M5 2.8h10v14.4l-2-1.2-1.7 1.2-1.6-1.2-1.7 1.2L6.3 16 5 17.2z" />
      <path d="M7.5 7h5M7.5 10h5" />
    </Icon>
  ),
  Building: (p: P) => (
    <Icon {...p}>
      <path d="M4 17V4.5A1.5 1.5 0 0 1 5.5 3h6A1.5 1.5 0 0 1 13 4.5V17M13 8h2.5A1.5 1.5 0 0 1 17 9.5V17M2.5 17h15" />
      <path d="M7 6.5h3M7 9.5h3M7 12.5h3" />
    </Icon>
  ),
  Bill: (p: P) => (
    <Icon {...p}>
      <rect x="3" y="4.5" width="14" height="11" rx="1.5" />
      <path d="M3 8h14M6 12h3" />
    </Icon>
  ),
  Bank: (p: P) => (
    <Icon {...p}>
      <path d="M2.8 7.5L10 3.5l7.2 4H2.8zM4.5 7.5v7M8 7.5v7M12 7.5v7M15.5 7.5v7M2.8 16.5h14.4" />
    </Icon>
  ),
  Chart: (p: P) => (
    <Icon {...p}>
      <path d="M3 17h14" />
      <rect x="4.5" y="9" width="2.6" height="6" rx=".6" />
      <rect x="8.7" y="5" width="2.6" height="10" rx=".6" />
      <rect x="12.9" y="11" width="2.6" height="4" rx=".6" />
    </Icon>
  ),
  Dashboard: (p: P) => (
    <Icon {...p}>
      <rect x="3" y="3" width="14" height="14" rx="2" />
      <path d="M3 8h14M9 8v9" />
      <path d="M11.5 14l1.7-2 1.3 1.2 1.5-2" />
    </Icon>
  ),
  Shield: (p: P) => (
    <Icon {...p}>
      <path d="M10 2.8l6 2.4v4.3c0 3.7-2.5 6.4-6 7.7-3.5-1.3-6-4-6-7.7V5.2z" />
      <path d="M7.3 10l1.9 1.9 3.6-3.7" />
    </Icon>
  ),
  Gear: (p: P) => (
    <Icon {...p}>
      <circle cx="10" cy="10" r="2.5" />
      <path d="M10 2.5v2M10 15.5v2M17.5 10h-2M4.5 10h-2M15.3 4.7l-1.4 1.4M6.1 13.9l-1.4 1.4M15.3 15.3l-1.4-1.4M6.1 6.1L4.7 4.7" />
    </Icon>
  ),
  Logout: (p: P) => (
    <Icon {...p}>
      <path d="M8 16.5H4.5A1.5 1.5 0 0 1 3 15V5a1.5 1.5 0 0 1 1.5-1.5H8M12.5 13.5L16 10l-3.5-3.5M16 10H7.5" />
    </Icon>
  ),
  Plus: (p: P) => (
    <Icon {...p}>
      <path d="M10 4.5v11M4.5 10h11" />
    </Icon>
  ),
  Search: (p: P) => (
    <Icon {...p}>
      <circle cx="9" cy="9" r="5.2" />
      <path d="M13 13l3.8 3.8" />
    </Icon>
  ),
  Chevron: (p: P) => (
    <Icon {...p}>
      <path d="M7.5 5l5 5-5 5" />
    </Icon>
  ),
  ChevronDown: (p: P) => (
    <Icon {...p}>
      <path d="M5.5 8l4.5 4.5L14.5 8" />
    </Icon>
  ),
  Arrow: (p: P) => (
    <Icon {...p}>
      <path d="M4 10h11.5M11 5.5l4.5 4.5-4.5 4.5" />
    </Icon>
  ),
  Upload: (p: P) => (
    <Icon {...p}>
      <path d="M10 13V3.5M6 7.5l4-4 4 4M3.5 13v2.5A1.5 1.5 0 0 0 5 17h10a1.5 1.5 0 0 0 1.5-1.5V13" />
    </Icon>
  ),
  Refresh: (p: P) => (
    <Icon {...p}>
      <path d="M16.5 10A6.5 6.5 0 1 1 14.6 5.4M16.5 3.5v3.8h-3.8" />
    </Icon>
  ),
  Chain: (p: P) => (
    <Icon {...p}>
      <path d="M8.5 11.5a3 3 0 0 0 4.2 0l2.6-2.6a3 3 0 0 0-4.2-4.2l-1 1" />
      <path d="M11.5 8.5a3 3 0 0 0-4.2 0l-2.6 2.6a3 3 0 0 0 4.2 4.2l1-1" />
    </Icon>
  ),
  Alert: (p: P) => (
    <Icon {...p}>
      <path d="M10 3.2l7.2 12.6a.8.8 0 0 1-.7 1.2H3.5a.8.8 0 0 1-.7-1.2z" />
      <path d="M10 8v3.5M10 14.2v.1" />
    </Icon>
  ),
  Check: (p: P) => (
    <Icon {...p}>
      <path d="M4.5 10.5l3.5 3.5 7.5-8" />
    </Icon>
  ),
  X: (p: P) => (
    <Icon {...p}>
      <path d="M5 5l10 10M15 5L5 15" />
    </Icon>
  ),
  Bolt: (p: P) => (
    <Icon {...p}>
      <path d="M11 2.8L4.5 11H10l-1 6.2 6.5-8.2H10z" />
    </Icon>
  ),
  Clock: (p: P) => (
    <Icon {...p}>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6v4l2.6 1.6" />
    </Icon>
  ),
  Key: (p: P) => (
    <Icon {...p}>
      <circle cx="6.5" cy="13.5" r="3" />
      <path d="M8.6 11.4L16 4M13.5 6.5l2 2M11.5 8.5l1.5 1.5" />
    </Icon>
  ),
  Send: (p: P) => (
    <Icon {...p}>
      <path d="M16.8 3.2L8.6 11.4M16.8 3.2l-5.2 13.6-3-5.4-5.4-3z" />
    </Icon>
  ),
  Trash: (p: P) => (
    <Icon {...p}>
      <path d="M3.5 5.5h13M8 5.5V3.8h4v1.7M5.2 5.5l.8 11h8l.8-11" />
    </Icon>
  ),
  Copy: (p: P) => (
    <Icon {...p}>
      <rect x="7" y="7" width="9.5" height="9.5" rx="1.5" />
      <path d="M13 7V4.5A1.5 1.5 0 0 0 11.5 3h-7A1.5 1.5 0 0 0 3 4.5v7A1.5 1.5 0 0 0 4.5 13H7" />
    </Icon>
  ),
  Undo: (p: P) => (
    <Icon {...p}>
      <path d="M7 4.5L3.5 8 7 11.5" />
      <path d="M3.5 8H12a4.5 4.5 0 0 1 0 9H8" />
    </Icon>
  ),
  Lock: (p: P) => (
    <Icon {...p}>
      <rect x="4" y="9" width="12" height="8.5" rx="1.5" />
      <path d="M6.8 9V6.5a3.2 3.2 0 0 1 6.4 0V9" />
    </Icon>
  ),
  Unlock: (p: P) => (
    <Icon {...p}>
      <rect x="4" y="9" width="12" height="8.5" rx="1.5" />
      <path d="M6.8 9V6.5a3.2 3.2 0 0 1 6.2-1.1" />
    </Icon>
  ),
  Grip: (p: P) => (
    <Icon {...p}>
      <circle cx="7.5" cy="5" r=".9" fill="currentColor" />
      <circle cx="12.5" cy="5" r=".9" fill="currentColor" />
      <circle cx="7.5" cy="10" r=".9" fill="currentColor" />
      <circle cx="12.5" cy="10" r=".9" fill="currentColor" />
      <circle cx="7.5" cy="15" r=".9" fill="currentColor" />
      <circle cx="12.5" cy="15" r=".9" fill="currentColor" />
    </Icon>
  ),
  More: (p: P) => (
    <Icon {...p}>
      <circle cx="5" cy="10" r="1" fill="currentColor" />
      <circle cx="10" cy="10" r="1" fill="currentColor" />
      <circle cx="15" cy="10" r="1" fill="currentColor" />
    </Icon>
  ),
  Present: (p: P) => (
    <Icon {...p}>
      <rect x="2.8" y="3.5" width="14.4" height="10" rx="1.5" />
      <path d="M8.5 6.5v4l3.2-2zM7 17h6M10 13.5V17" />
    </Icon>
  ),
  Pin: (p: P) => (
    <Icon {...p}>
      <path d="M7.5 3h5l-.8 4.5 2.8 2.5v1.5h-9V10l2.8-2.5zM10 11.5V17" />
    </Icon>
  ),
  Download: (p: P) => (
    <Icon {...p}>
      <path d="M10 3.5V13M6 9l4 4 4-4M3.5 13.5V15.5A1.5 1.5 0 0 0 5 17h10a1.5 1.5 0 0 0 1.5-1.5v-2" />
    </Icon>
  ),
  Printer: (p: P) => (
    <Icon {...p}>
      <path d="M6 7V3h8v4M6 14H4.5A1.5 1.5 0 0 1 3 12.5v-4A1.5 1.5 0 0 1 4.5 7h11A1.5 1.5 0 0 1 17 8.5v4a1.5 1.5 0 0 1-1.5 1.5H14" />
      <rect x="6" y="11.5" width="8" height="5.5" rx=".8" />
    </Icon>
  ),
  Box: (p: P) => (
    <Icon {...p}>
      <path d="M10 2.8l6.5 3.4v7.6L10 17.2l-6.5-3.4V6.2z" />
      <path d="M3.5 6.2L10 9.6l6.5-3.4M10 9.6v7.6" />
    </Icon>
  ),
  Menu: (p: P) => (
    <Icon {...p}>
      <path d="M3.5 6h13M3.5 10h13M3.5 14h13" />
    </Icon>
  ),
  Close: (p: P) => (
    <Icon {...p}>
      <path d="M5 5l10 10M15 5L5 15" />
    </Icon>
  ),
  Logo:({ size = 22, ...p }: P) => (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden {...p}>
      <rect width="32" height="32" rx="8" fill="#1f5e4a" />
      <path d="M6.5 16c0-3 2.2-5 4.7-5 4.6 0 4 10 8.6 10 2.5 0 4.7-2 4.7-5s-2.2-5-4.7-5c-4.6 0-4 10-8.6 10-2.5 0-4.7-2-4.7-5z" fill="none" stroke="#a7e6c8" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  ),
};
